// Domain types for the Loyalty **Coupons** surface.
//
// The shape is derived from the design in
// `docs/pantry/2026.05.19/breadstack-loyalty-pro (1).html` (#panel-rewards +
// #addRewardModal). The renamed surface is "Coupons" but legacy IDs in the HTML
// still use the word "reward" — we keep the new naming everywhere on the client.
//
// The per-store-type back-end endpoints (Cova / Greenline / Flowhub /
// Lightspeed / Shopify) historically used a richer cannabis-retail shape
// (applied_brands, applied_categories, applied_products, exclusions, etc.).
// The new Loyalty Coupons surface intentionally exposes a simplified
// member-facing shape; per-store adapters translate between the two.

export type LoyaltyCouponStoreType =
  | 'cova'
  | 'greenline'
  | 'flowhub'
  | 'lightspeed'
  | 'shopify'
  | 'woocommerce'

// Mirrors the legacy `coupon_status` field returned by the Cova coupon API —
// see `frontend/src/pages/Coupon/CovaCouponList/Table/index.js`. The previous
// 3-state design ('active' | 'paused' | 'draft') was speculative; the real
// back-end only ships `'active'` / `'expired'`.
export type LoyaltyCouponStatus = 'active' | 'expired'

export type LoyaltyCouponDiscountType = 'fixed' | 'percent'

// For a whole-store ('all') FIXED coupon, the merchant now explicitly picks
// whether the amount comes off the whole cart once ('cart') or off each
// matching item ('item'). This choice used to be auto-derived (All Products +
// Fixed always meant a cart-total deal); it is now an explicit control. Only
// meaningful when `discountType === 'fixed'` AND `appliesTo === 'all'` —
// categories/products fixed coupons are always per-item, and percentages are
// identical per-item vs per-cart so the field is ignored for them.
export type LoyaltyCouponFixedTarget = 'cart' | 'item'

// 'all' = applies storewide (back-end's `all_products` sentinel); 'categories'
// = explicit category list; 'products' = explicit product list. The old shape
// used `'products' + empty array` to encode "storewide" — that's no longer the
// case after the Coupon dialog adopted the 3-tab Applies-to UI. The inbound
// mapper now resolves `all_products` straight to `'all'`.
//
// The 3-tab modal can only **edit** these 3 modes. The full backend enum
// `LoyaltyCouponAppliedToWire` below has 5 values (it also includes
// `'specific_brands'` and `'cart_total'`) — those legacy modes are preserved
// on the domain via `LoyaltyCoupon.appliedToRaw` so list/grid display the
// correct label, but they collapse to `'all'` on the editable `appliesTo`
// field so the modal has a sensible default tab.
//
// NOTE on discount_type: the wire `discount_type` (and its paired `applied_to`)
// is *derived* from this Applies-to mode + the fixed/percent toggle + the
// `fixedTarget` choice in `mappers.ts`. When the scope is `'all'` and the
// discount is fixed, the merchant's `fixedTarget` selects the branch:
// `'cart'` → `discount_type: 'fixed_cart'` + `applied_to: 'cart_total'` (they
// travel together — a single amount off the whole cart); `'item'` →
// `discount_type: 'fixed_product'` + `applied_to: 'all_products'` (the amount
// comes off each item store-wide). `'categories'` / `'products'` + fixed
// always emit `'fixed_product'` (per-item); percent emits `'percent'` for
// every scope (with `applied_to` following the scope). Inbound, a `fixed_cart`
// coupon resolves back to `'all'` + Fixed + `fixedTarget: 'cart'`; any other
// fixed wire resolves to `fixedTarget: 'item'`.
export type LoyaltyCouponAppliesToMode = 'all' | 'categories' | 'products'

// Full back-end `applied_to` enum (Cova/Woo coupon storage). Mirrors:
//   class AppliedTo:
//     SPECIFIC_PRODUCTS    = "specific_products"
//     SPECIFIC_BRANDS      = "specific_brands"
//     SPECIFIC_CATEGORIES  = "specific_categories"
//     CART_TOTAL           = "cart_total"
//     ALL_PRODUCTS         = "all_products"
// `specific_brands` and `cart_total` are legacy cannabis-retail modes that
// the new 3-tab Applies-to UI does not edit; preserved here for list/grid
// display only.
export type LoyaltyCouponAppliedToWire =
  | 'all_products'
  | 'specific_categories'
  | 'specific_products'
  | 'specific_brands'
  | 'cart_total'

// Human-readable labels for each wire enum value — mirrors the legacy
// `transformTextToTitle` output in
// `frontend/src/pages/Coupon/CovaCouponList/Table/index.js`.
export const LOYALTY_COUPON_APPLIED_TO_LABEL: Record<
  LoyaltyCouponAppliedToWire,
  string
> = {
  all_products: 'All Products',
  specific_categories: 'Specific Categories',
  specific_products: 'Specific Products',
  specific_brands: 'Specific Brands',
  cart_total: 'Cart Total',
}

// Static category keys mirrored from the HTML chip list. Kept as a closed set
// so the filter dropdown stays consistent; web-category picker handles
// arbitrary IDs from the Categories module separately.
export type LoyaltyCouponCategoryKey =
  | 'bread'
  | 'pastry'
  | 'beverage'
  | 'merch'
  | 'gift-card'

export const LOYALTY_COUPON_CATEGORY_KEYS: LoyaltyCouponCategoryKey[] = [
  'bread',
  'pastry',
  'beverage',
  'merch',
  'gift-card',
]

export const LOYALTY_COUPON_CATEGORY_LABEL: Record<
  LoyaltyCouponCategoryKey,
  string
> = {
  bread: 'Bread',
  pastry: 'Pastry',
  beverage: 'Beverage',
  merch: 'Merch',
  'gift-card': 'Gift Card',
}

export const LOYALTY_COUPON_CATEGORY_BG: Record<
  LoyaltyCouponCategoryKey,
  string
> = {
  bread: '#FEF3C7',
  pastry: '#FDF2F8',
  beverage: '#EFF6FF',
  merch: '#F5F3FF',
  'gift-card': '#F0FDFA',
}

export const LOYALTY_COUPON_CATEGORY_ICON: Record<
  LoyaltyCouponCategoryKey,
  string
> = {
  bread: '🥖',
  pastry: '🥐',
  beverage: '☕',
  merch: '🛍️',
  'gift-card': '🎁',
}

// Fallback glyph shown whenever a coupon has no `icon` set — matches the
// ticket emoji used as the mockup's own fallback in `saveCoupon()`.
export const DEFAULT_LOYALTY_COUPON_ICON = '🎟️'

// Curated emoji choices offered by the Coupon Icon picker: every category
// icon plus the default ticket glyph.
export const LOYALTY_COUPON_ICON_CHOICES: string[] = [
  ...LOYALTY_COUPON_CATEGORY_KEYS.map(
    (key) => LOYALTY_COUPON_CATEGORY_ICON[key],
  ),
  DEFAULT_LOYALTY_COUPON_ICON,
]

export interface LoyaltyCouponWebCategoryRef {
  id: string
  displayName: string
}

// Cova's coupon endpoint requires each entry in `applied_products` to carry
// its product `type` (`'simple' | 'variable'`) and, for variable products,
// the full list of variation IDs. Mirrors the legacy contract in
// `frontend/src/pages/Coupon/hooks/mapper.js#revertProductsToApi` —
// `{ '<productId>': { type, variations: [{ id }, …] } }`.
//
// Both fields are optional on the domain ref so callers that only care about
// id/name (e.g. card view chips) don't need to populate them; the outbound
// Cova mapper falls back to `{ type: 'variable', variations: [] }` when they
// are missing.
export interface LoyaltyCouponProductVariationRef {
  id: string
}

export interface LoyaltyCouponProductRef {
  id: string
  name: string
  type?: 'simple' | 'variable'
  variations?: LoyaltyCouponProductVariationRef[]
}

// "Limitations & Restrictions" block on the coupon modal — design reference:
// `docs/pantry/2026.05.27/breadstack-loyalty-pro.html` lines 1049-1188.
//
// Fields fall into two groups:
//   1. Backend-supported today — wire through `mappers.ts` to the Cova/Woo
//      coupon endpoints (existing fields `usage_limit`, `usage_limit_per_user`,
//      `minimum_amount`, `maximum_amount`, `exclude_sale_items`,
//      `individual_use`, `date_expires`).
//   2. Not-yet-supported — kept on the interface for forward compatibility
//      but commented out in the UI and skipped in the mapper payload until
//      the backend coupon schema lands them. Re-enable in lockstep with the
//      backend agent.
export type LoyaltyCouponFrequency = 'any' | 'day' | 'week' | 'month'
export type LoyaltyCouponExpiryNotifyDays = 0 | 1 | 3 | 7 | 14

export interface LoyaltyCouponLimits {
  // ── Expiry ────────────────────────────────────────────────────────────────
  // ISO datetime; `null` means "Does not expire" (the design's default state).
  expiresAt: string | null
  // ── Usage caps ────────────────────────────────────────────────────────────
  // `null` = unlimited / no cap.
  perPerson: number | null
  total: number | null
  // ── Order / item caps ─────────────────────────────────────────────────────
  minOrder: number | null
  maxDiscount: number | null
  // ── Toggles ───────────────────────────────────────────────────────────────
  excludeSale: boolean
  // `stackable === !individual_use` on the Cova wire (note the inversion).
  stackable: boolean

  // ── Not yet supported by backend — leave on the interface so the UI can
  //    be re-enabled in one diff, but the mapper currently skips these.
  // expiryNotifyDays: LoyaltyCouponExpiryNotifyDays | null
  // perDay: number | null
  // frequency: LoyaltyCouponFrequency
  // excludeGiftCards: boolean
  // stackMember: boolean
  // inStoreOnly: boolean
  // firstOrderOnly: boolean
}

export const DEFAULT_LOYALTY_COUPON_LIMITS: LoyaltyCouponLimits = {
  expiresAt: null,
  perPerson: null,
  total: null,
  minOrder: null,
  maxDiscount: null,
  // Defaults mirror the toggles' initial `.on` state in the HTML mock
  // (line 1148 + 1169 are `on`; line 1162, 1176, 1183 are off).
  excludeSale: true,
  stackable: false,
  // Not-yet-supported defaults — kept for when the fields come back online:
  // expiryNotifyDays: 7,
  // perDay: null,
  // frequency: 'any',
  // excludeGiftCards: true,
  // stackMember: true,
  // inStoreOnly: false,
  // firstOrderOnly: false,
}

export interface LoyaltyCoupon {
  id: string
  // The shareable coupon identifier (e.g. `SOURDOUGH5`). Stored upper-case.
  code: string
  name: string
  description: string
  appliesTo: LoyaltyCouponAppliesToMode
  // Raw `applied_to` value from the wire, preserved for **display only**.
  // `specific_brands` and `cart_total` cannot be edited via the 3-tab modal —
  // `appliesTo` collapses them to `'all'`, but the list/grid still need the
  // original label so we keep the wire enum alongside.
  appliedToRaw?: LoyaltyCouponAppliedToWire
  categories: LoyaltyCouponCategoryKey[]
  webCategories: LoyaltyCouponWebCategoryRef[]
  products: LoyaltyCouponProductRef[]
  discountType: LoyaltyCouponDiscountType
  // Whole-cart vs per-item basis for a whole-store fixed discount. Only
  // meaningful when `discountType === 'fixed'` (and, in practice, when
  // `appliesTo === 'all'`). Seeded on Edit from the wire `discount_type` so the
  // modal can re-open a `fixed_cart` coupon on the "Cart total" option.
  fixedTarget: LoyaltyCouponFixedTarget
  // For `percent`, expected 0-100; for `fixed` the raw dollar amount.
  value: number
  status: LoyaltyCouponStatus
  redeemedCount: number
  // ISO datetime — when the coupon expires. Empty / undefined means "no expiry".
  // Kept on the domain type for backwards compat with card / list rows that
  // read it directly. New code should read `limits.expiresAt` instead.
  dateExpires?: string
  // Limitations & Restrictions — see `LoyaltyCouponLimits` for the contract.
  limits: LoyaltyCouponLimits
  // User-selected emoji shown as the coupon's badge in the grid/list views.
  // Empty/undefined means "use the default ticket icon" (`🎟️`) at render time.
  icon?: string
  // Customer eligibility — the list of member emails this coupon is restricted
  // to. Empty means "all customers" (no restriction). Maps to the wire field
  // `email_restrictions`. Names are not stored; chips display the raw email.
  emailRestrictions: string[]
  // Card visual hints — derived from the primary category so cards in the
  // grid get coloured backgrounds without per-coupon storage on the wire.
  bg?: string
  createdAt?: string
  updatedAt?: string
}

export interface LoyaltyCouponDraft {
  code: string
  name: string
  description: string
  // Empty string means "no icon chosen" — the default ticket icon renders
  // client-side; the wire value stored is `''`, never the fallback glyph.
  icon?: string
  appliesTo: LoyaltyCouponAppliesToMode
  webCategories: LoyaltyCouponWebCategoryRef[]
  // Always reflects an explicit selection — the "All products" case is now
  // `appliesTo === 'all'` with an empty `products[]`. Empty arrays in
  // `'products'` mode are a validation error.
  products: LoyaltyCouponProductRef[]
  discountType: LoyaltyCouponDiscountType
  // Whole-cart vs per-item basis for a whole-store fixed discount — read by the
  // mapper's `toWireDiscount`. Only matters when `discountType === 'fixed'` and
  // `appliesTo === 'all'`; ignored otherwise (categories/products fixed are
  // always per-item; percent is basis-agnostic).
  fixedTarget: LoyaltyCouponFixedTarget
  value: number
  // ISO datetime (`YYYY-MM-DDTHH:mm`) or empty string for no expiry.
  // Deprecated — kept as a write-through to `limits.expiresAt` so older
  // callers don't break. New code should write `limits.expiresAt` only.
  dateExpires?: string
  limits: LoyaltyCouponLimits
  // Customer eligibility — member emails this coupon is restricted to. Empty
  // array = "all customers". Persisted as the wire field `email_restrictions`.
  emailRestrictions: string[]
}

export interface LoyaltyCouponListParams {
  page: number
  page_size: number
  search?: string
  // Web-category ID (from the Categories module) or 'all'. Per-store-type
  // adapters translate this to whatever shape the back-end coupon filter expects.
  category?: string | 'all'
  discount_type?: LoyaltyCouponDiscountType | 'all'
  status?: LoyaltyCouponStatus | 'all'
  // Pantry-only. Filters coupons by their loyalty origin. Sent only for pantry
  // stores (including 'all', which explicitly overrides the backend's pantry
  // default of hiding loyalty-point coupons). The backend maps the singular
  // 'loyalty_point' token to the stored `coupon_type == 'loyalty_points'`.
  coupon_type?: 'all' | 'loyalty_point' | 'non_loyalty'
}

export interface LoyaltyCouponListResponse {
  items: LoyaltyCoupon[]
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
}
